import {
  DiscountCodeGenerationStrategy,
  RedoDiscount,
  RedoDiscountConfiguration,
} from "@redotech/redo-merchant-marketing-rpc/schema/discounts/discount-schemas";
import {
  DiscountGroupType,
  DiscountMinRequirementType,
  DiscountProvider,
  DiscountValueType,
  ExpirationType,
} from "@redotech/redo-model/discount";
import {
  DiscountCategory,
  DiscountSettingsType,
} from "@redotech/redo-model/discount/discount-db-parser";
import { assertNever } from "@redotech/util/type";
import isInteger from "lodash/isInteger";
import { CustomerEligibility } from "../discount-selector";

export interface DiscountValidationParams {
  discountName: string;
  discountAmount?: number;
  discountValueType: DiscountValueType;
  codeGenerationStrategy: DiscountGroupType;
  codeStrategyName: string;
  combineDiscountWith: {
    orderDiscounts: boolean;
    productDiscounts: boolean;
    shippingDiscount: boolean;
  };
  excludeOnSale: boolean;
  specifyCollections: boolean;
  specifyProducts: boolean;
  collectionIds: string[];
  productIds: string[];
  expiration: RedoDiscount["expiration"];
  minimumQuantity?: number;
  minimumSubtotal?: number;
  customerEligibility: CustomerEligibility;
  selectedCustomerIds: string[];
  limitToSegments?: string[];
  totalUsageLimit?: number;
  appliesOncePerCustomer: boolean;
  fullStartDateTime?: Date;
  existingDiscount?: RedoDiscount;
  existingDiscounts?: RedoDiscount[];
  category?: DiscountCategory;
}

export function validateDiscountConfiguration(
  params: DiscountValidationParams,
): RedoDiscountConfiguration | string {
  const {
    discountName,
    discountAmount,
    discountValueType,
    codeGenerationStrategy,
    codeStrategyName,
    combineDiscountWith,
    excludeOnSale,
    specifyCollections,
    specifyProducts,
    collectionIds,
    productIds,
    expiration,
    minimumQuantity,
    minimumSubtotal,
    customerEligibility,
    selectedCustomerIds,
    limitToSegments,
    totalUsageLimit,
    appliesOncePerCustomer,
    fullStartDateTime,
    existingDiscount,
    existingDiscounts,
    category,
  } = params;

  if (excludeOnSale && specifyCollections) {
    return "Invalid state";
  }

  if (existingDiscount) {
    if (
      codeGenerationStrategy !==
      existingDiscount.codeGenerationStrategy.strategy
    ) {
      return "You cannot change the type of a discount once it is created";
    }
    if (
      codeStrategyName.toLowerCase() !==
      existingDiscount.codeGenerationStrategy.code.toLowerCase()
    ) {
      return "You cannot change the code of a discount once it is created";
    }
  }

  // Static code uniqueness check
  if (codeGenerationStrategy === DiscountGroupType.STATIC) {
    const lowerCaseStrategyName = codeStrategyName.toLowerCase();
    const hasStaticNamingConflict = existingDiscounts?.some(
      (loadedDiscount) => {
        return (
          loadedDiscount.codeGenerationStrategy.strategy ===
            DiscountGroupType.STATIC &&
          loadedDiscount.codeGenerationStrategy.code.toLowerCase() ===
            lowerCaseStrategyName &&
          loadedDiscount._id !== existingDiscount?._id
        );
      },
    );
    if (hasStaticNamingConflict) {
      return "Static code names must be unique";
    }
  }

  // Basic field validations
  if (!discountName) {
    return "The discount must have a name";
  }

  if (!discountAmount || discountAmount < 0) {
    return "The discount amount must be greater than 0";
  }

  if (
    discountValueType === DiscountValueType.PERCENTAGE &&
    discountAmount > 100
  ) {
    return "A percentage must be between 1 and 100";
  }

  if (
    discountValueType === DiscountValueType.PERCENTAGE &&
    !isInteger(discountAmount)
  ) {
    return "A percentage must be a whole number";
  }

  if (discountValueType === DiscountValueType.AMOUNT && discountAmount > 1000) {
    return "The discount amount must be less than $1000";
  }

  if (minimumQuantity !== undefined && minimumQuantity < 0) {
    return "Minimum quantity must be greater than or equal to 0";
  }

  if (minimumSubtotal !== undefined && minimumSubtotal < 0) {
    return "Minimum purchase amount must be greater than or equal to 0";
  }

  if (totalUsageLimit !== undefined && totalUsageLimit < 0) {
    return "Total number of uses must be greater than or equal to 0";
  }

  if (!codeStrategyName) {
    return "The discount code is required";
  }

  if (
    expiration.expirationType === ExpirationType.EXPIRATION_DAYS &&
    (!expiration.days || expiration.days <= 0)
  ) {
    return "Number of days must be greater than 0";
  }

  if (minimumQuantity && minimumSubtotal) {
    return "Use one of minimum quantity or purchase amount, not both";
  }

  const now = new Date();
  if (!existingDiscount && fullStartDateTime && fullStartDateTime < now) {
    return "Start date/time cannot be in the past";
  }

  if (
    expiration.expirationType === ExpirationType.DATE &&
    expiration.date < now
  ) {
    return "Expiration date cannot be in the past";
  }

  if (
    fullStartDateTime &&
    expiration.expirationType === ExpirationType.DATE &&
    fullStartDateTime >= expiration.date
  ) {
    return "Expiration date must be after the start date";
  }

  // Build the configuration
  const codeStrategy: DiscountCodeGenerationStrategy = (() => {
    switch (codeGenerationStrategy) {
      case DiscountGroupType.STATIC:
        return { strategy: DiscountGroupType.STATIC, code: codeStrategyName };
      case DiscountGroupType.DYNAMIC:
        return { strategy: DiscountGroupType.DYNAMIC, code: codeStrategyName };
      default:
        assertNever(codeGenerationStrategy);
    }
  })();

  const discountSettings: RedoDiscountConfiguration = {
    ...existingDiscount,
    name: discountName,
    provider: DiscountProvider.SHOPIFY,
    codeGenerationStrategy: codeStrategy,
    expiration,
    discountSettings: {
      settingsType: DiscountSettingsType.SHOPIFY_BASIC,
      discountValueType,
      discountValueAmount: discountAmount,
      combinesWith: combineDiscountWith,
      excludeItemsOnSale: excludeOnSale,
      appliesToCollections:
        specifyCollections && collectionIds.length > 0
          ? collectionIds
          : undefined,
      appliesToProducts:
        specifyProducts && productIds.length > 0 ? productIds : undefined,
      endsAt:
        expiration.expirationType === ExpirationType.DATE
          ? expiration.date
          : undefined,
      minimumRequirement: (() => {
        if (minimumQuantity && minimumQuantity > 0) {
          const result = {
            type: DiscountMinRequirementType.MINIMUM_QUANTITY,
            minimum: minimumQuantity,
          };
          return result;
        } else if (minimumSubtotal && minimumSubtotal > 0) {
          const result = {
            type: DiscountMinRequirementType.MINIMUM_SUBTOTAL,
            minimum: minimumSubtotal,
          };
          return result;
        }
        return undefined;
      })(),
      customerEligibility,
      limitToCustomers:
        customerEligibility === CustomerEligibility.SPECIFIC
          ? selectedCustomerIds
          : undefined,
      limitToSegments:
        customerEligibility === CustomerEligibility.SEGMENTS
          ? limitToSegments
          : undefined,
      totalUsageLimit:
        totalUsageLimit && totalUsageLimit > 0 ? totalUsageLimit : undefined,
      appliesOncePerCustomer,
      startsAt:
        fullStartDateTime || existingDiscount?.discountSettings.startsAt,
    },
    ...(category ? { category } : {}),
  };
  return discountSettings;
}
